import { motion } from "motion/react";
import { Users, Lightbulb, Brain, Zap, Target as TargetIcon } from "lucide-react";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";

export function About() {
  const values = [
    { icon: <Lightbulb />, title: "Innovation First", desc: "We are constantly evolving our curriculum to keep pace with the rapidly changing tech landscape." },
    { icon: <TargetIcon />, title: "Precision Education", desc: "Every lesson is data-driven and outcomes-oriented to ensure real skill development." },
    { icon: <Brain />, title: "Holistic Thinking", desc: "We teach students to think as systems engineers, understanding the 'why' before the 'how'." },
    { icon: <Users />, title: "Inclusivity", desc: "Technology is for everyone. We design programs that are accessible and engaging for all learners." },
  ];

  const milestones = [
    { year: '2015', title: 'The Genesis', desc: 'Started as a small weekend coding club in a community center with 12 students.' },
    { year: '2018', title: 'Global Reach', desc: 'Opened our first international branch and launched our school partnership program.' },
    { year: '2021', title: 'Curriculum Redesign', desc: 'Fully redesigned our pedagogy to align with our core cognitive development framework.' },
    { year: '2026', title: 'The Future', desc: 'Now training over 5,000 students across 10 countries with 50+ partner schools.' },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section
        className="relative py-20 md:py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: "url('Whisk_teensinclasscoding.jpeg')" }}
      >
        <div className="absolute inset-0 bg-[#0A1A2F]/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-7xl text-white mb-6 sm:mb-8 tracking-tight leading-tight">
              Our Story: <br />
              Building the <span className="text-[#5B9BD5]">Next Generation</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/80 leading-relaxed max-w-2xl mx-auto">
              At ASTRA, we believe that learning to code is the new literacy. Our mission is to empower children with the tools they need to navigate and lead the tech-driven future.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SmartBrains Philosophy */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FFD700] text-[#0A1A2F] px-4 py-1.5 rounded-full text-sm font-bold mb-6 sm:mb-8 uppercase tracking-wider">
                <Zap className="w-4 h-4" /> The ASTRA Philosophy
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#0A1A2F] mb-6 sm:mb-8 tracking-tight">Built on the <span className="text-[#2E5C8A]">ASTRA Framework</span></h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                We've worked with pedagogical experts to build ASTRA's own core framework into every lesson.
                Our methodology doesn't just focus on coding syntax; it focuses on cognitive development.
              </p>
              <div className="space-y-6">
                {[
                  "Cognitive Flexibility through Debugging",
                  "Pattern Recognition in Large Data Systems",
                  "Collaborative Innovation Models",
                  "Iterative Thinking Pathways"
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="w-10 h-10 bg-[#E8F4FB] rounded-xl flex items-center justify-center text-[#2E5C8A]">
                      <Brain className="w-6 h-6" />
                    </div>
                    <span className="text-lg font-bold text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#2E5C8A] p-8 sm:p-12 rounded-[32px] sm:rounded-[60px] text-white relative shadow-2xl overflow-hidden">
              <div className="absolute top-0 right-0 w-full h-full opacity-10">
                <div className="w-96 h-96 bg-[#00FFFF] rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-10 relative z-10">Our Core Values</h3>
              <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 relative z-10">
                {values.map((v, i) => (
                  <div key={i}>
                    <div className="w-12 h-12 bg-[#00FFFF] rounded-xl flex items-center justify-center text-gray-900 mb-4">
                      {v.icon}
                    </div>
                    <h4 className="text-xl font-bold mb-2">{v.title}</h4>
                    <p className="text-white/70 leading-relaxed text-sm">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl sm:text-4xl text-[#0A1A2F] mb-4 sm:mb-6">Our Journey</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">From a small startup to an international leader in STEM education.</p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute top-1/2 left-0 w-full h-1 bg-[#E8F4FB] -translate-y-1/2 hidden md:block" />

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {milestones.map((m, i) => (
                <div key={i} className="relative bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl group hover:border-[#5B9BD5] transition-all">
                  <div className="w-16 h-16 bg-[#5B9BD5] text-white rounded-2xl flex items-center justify-center font-black text-xl mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-blue-100">
                    {m.year}
                  </div>
                  <h4 className="text-xl font-bold text-[#0A1A2F] mb-4">{m.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global Impact */}
      <section className="py-10 md:py-14 bg-[#0A1A2F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 text-center">
            <div>
              <div className="text-5xl sm:text-6xl font-black text-[#00FFFF] mb-3 sm:mb-4">3</div>
              <div className="text-base sm:text-lg font-bold text-[#5B9BD5] uppercase tracking-widest">Counties</div>
            </div>
            <div>
              <div className="text-5xl sm:text-6xl font-black text-[#FFD700] mb-3 sm:mb-4">405+</div>
              <div className="text-base sm:text-lg font-bold text-[#5B9BD5] uppercase tracking-widest">Active Students</div>
            </div>
            <div>
              <div className="text-5xl sm:text-6xl font-black text-white mb-3 sm:mb-4">100%</div>
              <div className="text-base sm:text-lg font-bold text-[#5B9BD5] uppercase tracking-widest">Commitment to Excellence</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
